import { useState } from 'react';
import { useApi } from '../api/client';
import type { ApiError } from '../api/client';

export type PerformanceSummary = {
  employeeUpn: string;
  employeeDisplayName: string;
  periodId: number | null;
  periodName: string | null;
  status: 'on_track' | 'mixed' | 'at_risk' | 'insufficient_data' | string;
  headline: string;
  bullets: string[];
  modelCalled: boolean;
  stats?: {
    totalAssignments: number;
    selfRated: number;
    managerRated: number;
    avgSelf: number | null;
    avgManager: number | null;
  };
};

type Props = {
  employeeUpn: string;
  employeeName?: string;
  periodId?: number | null;
  compact?: boolean;
};

const STATUS_META: Record<string, { label: string; bg: string; fg: string; border: string }> = {
  on_track: { label: 'On track', bg: '#ecfdf5', fg: '#047857', border: '#a7f3d0' },
  mixed: { label: 'Mixed', bg: '#fff7ed', fg: '#c2410c', border: '#fed7aa' },
  at_risk: { label: 'At risk', bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca' },
  insufficient_data: { label: 'Not enough data', bg: '#f8fafc', fg: '#475569', border: '#e2e8f0' },
};

export default function PerformanceSummaryCard({
  employeeUpn,
  employeeName,
  periodId,
  compact = false,
}: Props) {
  const api = useApi();
  const [data, setData] = useState<PerformanceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    if (!employeeUpn) return;
    setLoading(true);
    setError(null);
    try {
      const body: { employeeUpn: string; periodId?: number } = { employeeUpn };
      if (periodId != null) body.periodId = periodId;
      const res = (await api.post('/performance/summary', body)) as PerformanceSummary;
      setData(res);
    } catch (e) {
      const err = e as ApiError;
      setError(err?.message || 'Could not generate summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  const meta = STATUS_META[data?.status || ''] || STATUS_META.insufficient_data;
  const titleName = data?.employeeDisplayName || employeeName || employeeUpn;

  return (
    <div
      style={{
        marginTop: compact ? 12 : 16,
        marginBottom: compact ? 12 : 16,
        padding: compact ? '12px 14px' : '14px 16px',
        borderRadius: 12,
        border: '1px solid #99f6e4',
        background: 'linear-gradient(135deg, #f0fdfa 0%, #fff 55%, #fff1f2 100%)',
        boxShadow: '0 1px 2px rgba(15, 118, 110, 0.06)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#0f766e',
            }}
          >
            Performance summary
          </div>
          <div style={{ fontSize: 14, color: '#334155', marginTop: 2 }}>
            Auto coach blurb for <strong>{titleName}</strong>
            {data?.periodName ? ` · ${data.periodName}` : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={loading || !employeeUpn}
          style={{
            padding: '7px 12px',
            fontSize: 13,
            fontWeight: 600,
            borderRadius: 8,
            border: '1px solid #14b8a6',
            background: loading ? '#ccfbf1' : '#0f766e',
            color: loading ? '#0f766e' : '#fff',
            cursor: loading || !employeeUpn ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? 'Generating…' : data ? 'Refresh' : 'Generate'}
        </button>
      </div>

      {error && <p style={{ margin: '10px 0 0', color: '#b91c1c', fontSize: 13 }}>{error}</p>}

      {!data && !loading && !error && (
        <p style={{ margin: '10px 0 0', color: '#64748b', fontSize: 13 }}>
          One-click plain-language read on trajectory, strengths, and risks — no blank Ask chat needed.
        </p>
      )}

      {data && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                background: meta.bg,
                color: meta.fg,
                border: `1px solid ${meta.border}`,
              }}
            >
              {meta.label}
            </span>
            {data.modelCalled === false && data.status !== 'insufficient_data' && (
              <span style={{ fontSize: 11, color: '#94a3b8' }}>rule-based (AI offline)</span>
            )}
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#0f172a', lineHeight: 1.4 }}>
            {data.headline}
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#334155', fontSize: 13, lineHeight: 1.45 }}>
            {(data.bullets || []).map((b, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                {b}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
